import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { scrollToId } from "../config";
import { MagneticButton } from "./MagneticButton";

const HERO_BG =
  "https://images.unsplash.com/photo-1741704340644-ec6359b7f996?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2x0ZW4lMjBtZXRhbCUyMGdsb3d8ZW58MHx8fHwxNzg3NjY1ODk1fDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];
const BASE_DELAY = 1.7;

const RevealLine = ({ children, index, lang }) => (
  <div className="overflow-hidden">
    <motion.div
      key={lang + index}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: BASE_DELAY + index * 0.14, ease: EASE }}
    >
      {children}
    </motion.div>
  </div>
);

const PhoneHero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 110,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 110,
    damping: 16,
  });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      style={{ perspective: 1200 }}
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 60, rotateX: 10 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 1.2, delay: BASE_DELAY + 0.5, ease: EASE }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="group relative mx-auto max-w-[300px] lg:max-w-[340px]"
      >
        <div className="absolute -inset-4 bg-gradient-to-r from-[#E65100]/20 to-[#C5A059]/20 blur-3xl opacity-60 transition-opacity duration-700 group-hover:opacity-100" />
        <div
          data-testid="hero-app-frame"
          className="relative rounded-[2.8rem] border border-[#2A2A2A] bg-[#050505] p-2 shadow-[0_40px_90px_rgba(0,0,0,0.7)]"
        >
          <div className="relative overflow-hidden rounded-[2.2rem] border border-[#1F1F1F]">
            <video
              data-testid="hero-app-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="block aspect-[640/1138] w-full bg-black object-cover"
            >
              <source src="/hero-demo.webm" type="video/webm" />
              <source src="/hero-demo.mp4" type="video/mp4" />
            </video>
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(197,160,89,0.16),transparent_60%)] opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { lang, t } = useLanguage();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 200]);
  const opacityBg = useTransform(scrollY, [0, 700], [1, 0.25]);

  return (
    <section
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-24 pt-36"
    >
      <motion.div style={{ y: yBg, opacity: opacityBg }} className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-[#0A0A0A] [mask-image:linear-gradient(to_top,black,transparent)]" />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            key={"eyebrow" + lang}
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: BASE_DELAY, ease: EASE }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="h-px w-12 bg-[#C5A059]" />
            <span className="font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-[#C5A059]">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1 className="font-bebas uppercase leading-[0.88] tracking-tight text-[#F5F5F5] [font-size:clamp(3.6rem,9.5vw,8.5rem)]">
            {t.hero.lines.map((line, i) => (
              <RevealLine key={`${lang}-${i}`} index={i} lang={lang}>
                <span className={i === t.hero.lines.length - 1 ? "text-[#C5A059]" : ""}>
                  {line}
                </span>
              </RevealLine>
            ))}
          </h1>

          <motion.p
            key={"serif" + lang}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: BASE_DELAY + 0.55, ease: EASE }}
            className="mt-6 font-cormorant text-2xl italic text-[#C5A059] lg:text-3xl"
          >
            {t.hero.serif}
          </motion.p>

          <motion.p
            key={"sub" + lang}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: BASE_DELAY + 0.7, ease: EASE }}
            className="mt-5 max-w-xl font-manrope text-sm leading-relaxed text-[#8A9199] md:text-base"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: BASE_DELAY + 0.85, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <MagneticButton>
              <button
                data-testid="hero-trial-cta"
                onClick={() => scrollToId("#contatto")}
                className="group relative inline-flex items-center gap-3 overflow-hidden bg-[#C5A059] px-8 py-4 font-manrope text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#B38E4A]"
              >
                {t.hero.ctaPrimary}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </MagneticButton>
            <button
              data-testid="hero-features-cta"
              onClick={() => scrollToId("#features")}
              className="group inline-flex items-center gap-3 border-b border-[#4A4D52] pb-1 font-manrope text-sm font-semibold uppercase tracking-[0.14em] text-[#F5F5F5] transition-colors duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
            >
              {t.hero.ctaSecondary}
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: BASE_DELAY + 1.05 }}
            className="mt-16 grid max-w-xl grid-cols-3 divide-x divide-[#2A2A2A] border-y border-[#2A2A2A]"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} className="px-5 py-5 first:pl-0">
                <p data-testid={`hero-stat-value-${i}`} className="font-bebas text-3xl text-[#F5F5F5] md:text-4xl">
                  {s.value}
                </p>
                <p className="mt-1 font-manrope text-[11px] uppercase tracking-[0.12em] text-[#5C636A]">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <PhoneHero />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: BASE_DELAY + 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="font-manrope text-[10px] uppercase tracking-[0.3em] text-[#5C636A]">
            {t.hero.scroll}
          </span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-px bg-[#C5A059]/60"
          />
        </div>
      </motion.div>
    </section>
  );
};
